import React, { useState, useEffect, useRef } from 'react';
import { getUrl } from 'aws-amplify/storage';
import { useParams, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import CourseReferencesGrid from '../components/CourseReferencesGrid';
import CourseRegistrationForm from '../components/CourseRegistrationForm';

interface Course {
  id: string;
  courseId?: string;
  title: string;
  punchline?: string;
  shortDescription?: string;
  description: string;
  price: string;
  tutorId?: string;
  tutor?: {
    id: string;
    name: string;
    photo?: string;
    bio?: string;
    shortBio?: string;
    role?: string;
    research?: string;
    projects?: string;
    references?: string;
    training?: string;
    isFemale?: boolean;
  };
  tutor2Id?: string;
  tutor2?: {
    id: string;
    name: string;
    photo?: string;
    bio?: string;
    shortBio?: string;
    role?: string;
    isFemale?: boolean;
  };
  location?: string;
  startDate?: string;
  endDate?: string;
  targetgroup?: string;
  urlSlug?: string;
  partnerId?: string;
  partner?: {
    id: string;
    name: string;
    logoPath?: string;
    website?: string;
  };
  partner2Id?: string;
  partner2?: {
    id: string;
    name: string;
    logoPath?: string;
    website?: string;
  };
  isExternalRegistration?: boolean;
  externalRegistrationUrl?: string;
  sessions?: Array<{
    id: string;
    title: string;
    date: string;
    startTime: string;
    endTime: string;
    locationType: 'ONLINE' | 'ON_SITE';
    location: string;
    description?: string;
    maxAttendees?: number;
    order?: number; // Session order/sequence
  }>;
  isActive: boolean;
  discountInformation?: string;
  videos?: Array<{
    id: string;
    url: string;
    heading?: string;
    isPrimary?: boolean;
    order?: number;
    isActive?: boolean;
  }>;
}



const CourseDetailPage: React.FC = () => {
  const { courseSlug } = useParams<{ courseSlug: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [discountInformation, setDiscountInformation] = useState<string | null>(null);

  const [tutorPhotoIndex, setTutorPhotoIndex] = useState<number>(0);
  const [tutorPhotoCandidates, setTutorPhotoCandidates] = useState<string[]>([]);
  const [tutor2PhotoIndex, setTutor2PhotoIndex] = useState<number>(0);
  const [tutor2PhotoCandidates, setTutor2PhotoCandidates] = useState<string[]>([]);
  const [heroBgUrl, setHeroBgUrl] = useState<string>('');
  const [heroBgLoaded, setHeroBgLoaded] = useState<boolean>(false);
  const heroRef = useRef<HTMLElement | null>(null);
  const [partnerLogoUrl, setPartnerLogoUrl] = useState<string>('');
  const [partner2LogoUrl, setPartner2LogoUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [courseVideos, setCourseVideos] = useState<Array<{ id: string; url: string; heading?: string; isPrimary?: boolean; order?: number; isActive?: boolean }>>([]);


  const client = generateClient<Schema>({ authMode: 'iam' });
  // Ensure hero overlaps header on mobile exactly like page-header
  useEffect(() => {
    const applyMobileHeroOffset = () => {
      if (!heroRef.current) return;
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (!isMobile) {
        heroRef.current.style.marginTop = '';
        heroRef.current.style.paddingTop = '';
        return;
      }
      const header = document.querySelector<HTMLElement>('.header-container');
      const headerHeight = header ? header.offsetHeight : 70;
      heroRef.current.style.marginTop = `-${headerHeight}px`;
      // Keep existing vertical padding but add headerHeight to top
      // Default mobile top padding similar to page-header (2rem)
      const baseTopPaddingRem = 2;
      const baseTopPaddingPx = baseTopPaddingRem * 16; // assume 16px root
      const computedTopPadding = headerHeight + baseTopPaddingPx;
      heroRef.current.style.paddingTop = `${computedTopPadding}px`;
    };

    applyMobileHeroOffset();
    window.addEventListener('resize', applyMobileHeroOffset, { passive: true });
    window.addEventListener('orientationchange', applyMobileHeroOffset, { passive: true } as any);
    return () => {
      window.removeEventListener('resize', applyMobileHeroOffset as any);
      window.removeEventListener('orientationchange', applyMobileHeroOffset as any);
    };
  }, []);

  // Function to fetch discount information separately
  const fetchDiscountInformation = async (courseId: string) => {
    try {
      const result = await client.models.Course.get({ id: courseId });
      if (result.data && (result.data as any).discountInformation) {
        setDiscountInformation((result.data as any).discountInformation);
      }
    } catch (error) {
      console.log('Could not fetch discount information:', error);
      // Silently fail - discount information is optional
    }
  };

  useEffect(() => {
    fetchCourseBySlug();
  }, [courseSlug]);

  useEffect(() => {
    (async () => {
      if (!course?.id) { setCourseVideos([]); return; }
      try {
        // Try to load via related field if present
        const related = (course as any).videos;
        if (Array.isArray(related) && related.length > 0) {
          // Filter out soft-deleted items and inactive ones; then deduplicate by URL (fallback to id)
          const filtered = (related as any[])
            .filter((v: any) => (v?.status ?? 'active') !== 'deleted')
            .filter((v: any) => v?.isActive ?? true)
            .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
          const seen = new Set<string>();
          const deduped = filtered.filter((v: any) => {
            const key = (v?.url || '').trim().toLowerCase() || `id:${v?.id || Math.random()}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          setCourseVideos(deduped as any);
          return;
        }
      } catch {}
      try {
        // Fallback: query CourseVideo by courseId
        if (!(client.models as any).CourseVideo) { setCourseVideos([]); return; }
        const res = await (client.models as any).CourseVideo.list({ filter: { courseId: { eq: course.id } }, limit: 200 });
        const filtered = (res?.data || [])
          .filter((v: any) => (v?.status ?? 'active') !== 'deleted')
          .filter((v: any) => v?.isActive ?? true)
          .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
        const seen = new Set<string>();
        const deduped = filtered.filter((v: any) => {
          const key = (v?.url || '').trim().toLowerCase() || `id:${v?.id || Math.random()}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setCourseVideos(deduped as any);
      } catch (e) {
        setCourseVideos([]);
      }
    })();
  }, [course?.id]);

  // Build robust tutor photo fallbacks when course changes (supports S3 public keys)
  useEffect(() => {
    (async () => {
      const raw = (course?.tutor?.photo || '').trim();
      if (!raw) {
        setTutorPhotoCandidates(['/assets/images/tutors/default-tutor.svg']);
        setTutorPhotoIndex(0);
        return;
      }

      const candidates: string[] = [];
      try {
        if (raw.startsWith('http') || raw.startsWith('/')) {
          candidates.push(raw);
        } else {
          // Attempt to resolve as S3 key under public access level
          try {
            const publicPath = raw.startsWith('public/') ? raw : `public/${raw}`;
            const { url } = await getUrl({ path: publicPath });
            candidates.push(url.toString());
          } catch {}
          // Also try legacy assets folders
          candidates.push(`/assets/images/tutors/${raw}`);
          candidates.push(`/assets/images/home/founders/${raw}`);
        }
      } finally {
        candidates.push('/assets/images/tutors/default-tutor.svg');
        setTutorPhotoCandidates(candidates);
        setTutorPhotoIndex(0);
      }
    })();
  }, [course?.tutor?.photo]);

  // Build tutor2 photo candidates when tutor2 changes
  useEffect(() => {
    (async () => {
      const raw = (course?.tutor2?.photo || '').trim();
      if (!raw) {
        setTutor2PhotoCandidates(['/assets/images/tutors/default-tutor.svg']);
        setTutor2PhotoIndex(0);
        return;
      }

      const candidates: string[] = [];
      try {
        if (raw.startsWith('http') || raw.startsWith('/')) {
          candidates.push(raw);
        } else {
          try {
            const publicPath = raw.startsWith('public/') ? raw : `public/${raw}`;
            const { url } = await getUrl({ path: publicPath });
            candidates.push(url.toString());
          } catch {}
          candidates.push(`/assets/images/tutors/${raw}`);
          candidates.push(`/assets/images/home/founders/${raw}`);
        }
      } finally {
        candidates.push('/assets/images/tutors/default-tutor.svg');
        setTutor2PhotoCandidates(candidates);
        setTutor2PhotoIndex(0);
      }
    })();
  }, [course?.tutor2?.photo]);

  // Use solid color background instead of hero image
  useEffect(() => {
    setHeroBgUrl('');
    setHeroBgLoaded(true);
  }, []);

  // Resolve partner logo S3 key if needed
  useEffect(() => {
    (async () => {
      const key = course?.partner?.logoPath || '';
      if (!key) { setPartnerLogoUrl(''); return; }
      try {
        if (key.startsWith('http') || key.startsWith('/')) {
          setPartnerLogoUrl(key);
        } else {
          // Handle both old and new S3 schemas
          let publicPath: string;
          if (key.startsWith('public/')) {
            publicPath = key;
          } else if (key.startsWith('partner-logos/')) {
            publicPath = `public/${key.replace('partner-logos/', 'partners/')}`;
          } else {
            publicPath = `public/${key}`;
          }
          const { url } = await getUrl({ path: publicPath });
          setPartnerLogoUrl(url.toString());
        }
      } catch (e) {
        setPartnerLogoUrl('');
      }
    })();
  }, [course?.partner?.logoPath]);

  // Resolve second partner logo S3 key if needed
  useEffect(() => {
    (async () => {
      const key = course?.partner2?.logoPath || '';
      if (!key) { setPartner2LogoUrl(''); return; }
      try {
        if (key.startsWith('http') || key.startsWith('/')) {
          setPartner2LogoUrl(key);
        } else {
          // Handle both old and new S3 schemas
          let publicPath: string;
          if (key.startsWith('public/')) {
            publicPath = key;
          } else if (key.startsWith('partner-logos/')) {
            publicPath = `public/${key.replace('partner-logos/', 'partners/')}`;
          } else {
            publicPath = `public/${key}`;
          }
          const { url } = await getUrl({ path: publicPath });
          setPartner2LogoUrl(url.toString());
        }
      } catch (e) {
        setPartner2LogoUrl('');
      }
    })();
  }, [course?.partner2?.logoPath]);





  const fetchCourseBySlug = async () => {
    if (!courseSlug) return;
    
    try {
      setLoading(true);
      
      // Fetch courses with partner relationships
      console.log('🔍 Fetching courses with partner relationships...');
      
      let data;
      try {
        // Fetch courses with partner and sessions data
        const result = await client.models.Course.list({
          selectionSet: [
            'id', 'courseId', 'title', 'punchline', 'description', 'price', 
            'location', 'startDate', 'endDate', 'targetgroup', 'urlSlug', 'isActive',
            // optional fields below may not exist on older schemas and can be fetched in the primary attempt only
            'isExternalRegistration', 'externalRegistrationUrl',
            'tutorId', 'tutor.id', 'tutor.name', 'tutor.photo', 'tutor.bio', 'tutor.shortBio', 'tutor.education', 'tutor.role', 'tutor.isFemale', 'tutor2Id',
            'partner.id', 'partner.name', 'partner.logoPath', 'partner.website', 'partnerId', 'partner2Id',
            'sessions.id', 'sessions.courseId', 'sessions.title', 'sessions.date', 'sessions.startTime', 
            'sessions.endTime', 'sessions.locationType', 'sessions.location', 
            'sessions.description', 'sessions.maxAttendees', 'sessions.order', 'sessions.status',
            // attempt to fetch related videos if the relation is available in schema
            'videos.id', 'videos.courseId', 'videos.url', 'videos.heading', 'videos.isPrimary', 'videos.order', 'videos.isActive', 'videos.status', 'videos.deletedAt'
          ]
        });
        data = result.data;
        console.log('✅ Courses with partner relationships fetched:', data?.length || 0);
      } catch (relationshipError) {
        console.log('⚠️ Partner relationship query failed, trying basic query:', relationshipError);
        // Fallback to basic query with sessions
        const result = await client.models.Course.list({
          selectionSet: [
            'id', 'courseId', 'title', 'punchline', 'description', 'price', 
            'location', 'startDate', 'endDate', 'targetgroup', 'urlSlug', 'isActive',
            // Fallback excludes fields that may not exist yet on the current local schema
            // 'isExternalRegistration', 'externalRegistrationUrl',
            'tutorId', 'tutor.id', 'tutor.name', 'tutor.photo', 'tutor.bio', 'tutor.shortBio', 'tutor.education', 'tutor2Id', 'partnerId', 'partner2Id',
            'partner.id', 'partner.name', 'partner.logoPath', 'partner.website',
            'sessions.id', 'sessions.courseId', 'sessions.title', 'sessions.date', 'sessions.startTime', 
            'sessions.endTime', 'sessions.locationType', 'sessions.location', 
            'sessions.description', 'sessions.maxAttendees', 'sessions.order', 'sessions.status'
          ]
        });
        data = result.data;
        console.log('✅ Basic courses fetched:', data?.length || 0);
      }

      if (data) {
        console.log('📋 Fetched courses:', data.length);
        console.log('🔍 Looking for slug:', courseSlug);
        
        // Debug: Log all course titles and their URL slugs
        data.forEach((c: any, index: number) => {
          console.log(`Course ${index + 1}: "${c.title}" → urlSlug: "${c.urlSlug || 'not-set'}" (active: ${c.isActive})`);
        });
        
        // Find course by matching urlSlug field
        const targetCourse = data.find((c: any) => 
          c.urlSlug === courseSlug && c.isActive
        );

        if (targetCourse) {
          console.log('✅ Found matching course:', targetCourse.title);
          console.log('📆 Course dates:', {
            startDate: targetCourse.startDate,
            endDate: targetCourse.endDate,
            startDateType: typeof targetCourse.startDate,
            endDateType: typeof targetCourse.endDate
          });
          console.log('🤝 Partner data:', {
            hasPartner: !!targetCourse.partner,
            partnerName: targetCourse.partner?.name,
            logoPath: targetCourse.partner?.logoPath,
            logoPathType: typeof targetCourse.partner?.logoPath
          });
          console.log('🤝 Partner2 linkage:', {
            partner2Id: (targetCourse as any)?.partner2Id || null
          });
          
          // Filter out soft-deleted sessions before setting course
          const totalSessions = targetCourse.sessions?.length || 0;
          if (targetCourse.sessions && Array.isArray(targetCourse.sessions)) {
            targetCourse.sessions = targetCourse.sessions.filter((session: any) => {
              return session.status !== 'deleted';
            });
          }
          const activeSessions = targetCourse.sessions?.length || 0;
          
          // Debug sessions data
          console.log('🔍 Sessions data:', {
            hasSessions: !!targetCourse.sessions,
            totalFetched: totalSessions,
            activeAfterFilter: activeSessions,
            deletedFiltered: totalSessions - activeSessions,
            sessionsArray: Array.isArray(targetCourse.sessions)
          });
          
          // Sessions are now fetched through the course relationship and filtered
          console.log('🔍 Course with filtered sessions:', targetCourse);
          setCourse(targetCourse as Course);
          setContentLoading(false); // Course data is loaded, content can be shown
          
          // Fetch discount information separately
          if (targetCourse.id) {
            fetchDiscountInformation(targetCourse.id);
          }

          // If a second partner is linked by ID, fetch its details
          try {
            const partner2Id = (targetCourse as any)?.partner2Id;
            if (partner2Id) {
              const partner2Res = await client.models.Partner.get({ id: partner2Id, selectionSet: ['id','name','logoPath','website'] as any });
              if (partner2Res?.data) {
                setCourse((prev) => prev ? ({ ...prev, partner2: partner2Res.data as any }) : prev);
              }
            }
          } catch (p2err) {
            console.warn('Could not fetch second partner details:', p2err);
          }
          
          // If a second tutor is linked by ID, fetch its details
          try {
            const tutor2Id = (targetCourse as any)?.tutor2Id;
            if (tutor2Id) {
              const tutor2Res = await client.models.Tutor.get({ id: tutor2Id, selectionSet: ['id','name','photo','bio','shortBio','role','isFemale'] as any });
              if (tutor2Res?.data) {
                setCourse((prev) => prev ? ({ ...prev, tutor2: tutor2Res.data as any }) : prev);
              }
            }
          } catch (t2err) {
            console.warn('Could not fetch second tutor details:', t2err);
          }

          // Update page title for SEO
          document.title = `${targetCourse.title} | MintKitchen`;
        } else {
          console.log('❌ No matching course found for slug:', courseSlug);
          console.log('Available slugs:', data
            .filter((c: any) => c.urlSlug && c.isActive)
            .map((c: any) => c.urlSlug)
          );
          setError('Course not found');
        }
      } else {
        console.log('❌ No data returned from GraphQL query');
        setError('No courses data available');
      }
    } catch (err) {
      console.error('🚨 Error fetching course:', err);
      console.error('Error details:', {
        message: (err as any)?.message,
        name: (err as any)?.name,
        stack: (err as any)?.stack
      });
      setError(`Failed to load course details: ${(err as any)?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const createSlug = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handlePartnerClick = (partner: { website?: string; name: string }) => {
    if (partner.website) {
      window.open(partner.website, '_blank', 'noopener,noreferrer');
    }
  };

  const handleImageDragStart = (e: React.DragEvent) => {
    e.preventDefault();
    return false;
  };

  const handleImageContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) {
      console.warn('⚠️ formatDate: Empty date string provided');
      return 'Date not set';
    }
    
    // Try multiple date parsing approaches
    let date = new Date(dateString);
    
    // If first attempt fails, try common formats
    if (isNaN(date.getTime())) {
      // Try ISO format (YYYY-MM-DD)
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        date = new Date(dateString + 'T00:00:00.000Z');
      }
      // Try German format (DD.MM.YYYY)
      else if (dateString.match(/^\d{1,2}\.\d{1,2}\.\d{4}$/)) {
        const [day, month, year] = dateString.split('.');
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
    }
    
    if (isNaN(date.getTime())) {
      console.warn('⚠️ formatDate: Invalid date string after all attempts:', dateString);
      return `Invalid date: ${dateString}`;
    }
    
    // Return short format DD.MM.YY
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const buildDisplayNameWithAcademicTitle = (person: any): string => {
    const baseName = person?.name || '';
    if (!baseName) return '';
    const education = (person?.education || '').toString();
    const shortBio = (person?.shortBio || '').toString();
    const source = `${education} ${shortBio}`;

    let display = baseName;

    // Prefix titles
    if (!/\b(Dr\.|Prof\.)\b/.test(baseName)) {
      if (/\b(Professor|Prof\.)\b/i.test(source)) {
        display = `Prof. ${display}`;
      } else if (/\bDr\.?\b/i.test(source)) {
        display = `Dr. ${display}`;
      }
    }

    // Suffix academic degree in parentheses if not already present
    if (!/\([^)]*\)/.test(baseName)) {
      let degreeSuffix = '';
      const paren = source.match(/\(([^)]+)\)/);
      if (paren && /[A-Za-z]/.test(paren[1])) {
        degreeSuffix = ` (${paren[1].trim()})`;
      } else {
        const s = source.toLowerCase();
        if (/m\.?\s*sc/.test(s)) degreeSuffix = ' (M. Sc.)';
        else if (/m\.?\s*ed/.test(s)) degreeSuffix = ' (M. Ed.)';
        else if (/m\.?\s*a\b/.test(s)) degreeSuffix = ' (M.A.)';
        else if (/b\.?\s*sc/.test(s)) degreeSuffix = ' (B. Sc.)';
        else if (/b\.?\s*a\b/.test(s)) degreeSuffix = ' (B.A.)';
        else if (/\bmba\b/.test(s)) degreeSuffix = ' (MBA)';
        else if (/ph\s?d/.test(s)) degreeSuffix = ' (Ph.D.)';
      }
      if (degreeSuffix) display = `${display}${degreeSuffix}`;
    }

    return display;
  };

  const formatTime = (timeString: string): string => {
    return timeString.slice(0, 5); // HH:MM format
  };

  const getTotalCourseHours = (sessions?: Array<{ startTime: string; endTime: string }>): number => {
    if (!sessions || sessions.length === 0) return 0;
    let totalMinutes = 0;
    for (const s of sessions) {
      const [sh, sm] = (s.startTime || '').split(':').map(Number);
      const [eh, em] = (s.endTime || '').split(':').map(Number);
      if (Number.isFinite(sh) && Number.isFinite(sm) && Number.isFinite(eh) && Number.isFinite(em)) {
        let diff = (eh * 60 + em) - (sh * 60 + sm);
        if (diff < 0) diff += 24 * 60; // handle overnight edge-case
        totalMinutes += diff;
      }
    }
    return Math.round(totalMinutes / 60);
  };

  // Don't show full page loading - show hero immediately
  // if (loading) {
  //   return (
  //     <div className="course-detail-loading">
  //       <div className="spinner"></div>
  //       <p>Loading course details...</p>
  //     </div>
  //   );
  // }

  return (
    <div className="page course-detail-page">
      {/* Hero Section - Always visible, loads immediately */}
      <section
        ref={(el) => { heroRef.current = el; }}
        className={`page-header course-hero ${!heroBgLoaded ? 'hero-bg-loading' : ''}`}
        style={{
          backgroundColor: '#008F99'
        }}
      >
        <div className="container">
          <div className="course-hero-content">
            {course && (
              <>
                <h1 className="course-hero-title">{course.title}</h1>
                {course.punchline && (
                  <p className="course-hero-subtitle">
                    {course.punchline.length > 180 ? `${course.punchline.slice(0, 180)}…` : course.punchline}
                  </p>
                )}
              </>
            )}
            
            {/* Discount Information - Temporarily disabled due to schema sync issue */}
            {/* {course.discountInformation && (
              <div className="discount-info">
                <div className="discount-badge">
                  <svg
                    className="discount-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  <span className="discount-text">{course.discountInformation}</span>
                </div>
              </div>
            )} */}

          </div>
        </div>
      </section>

      {/* Course Content */}
      {error ? (
        <section className="course-content">
          <div className="course-detail-error">
            <h2>Course Not Found</h2>
            <p>{error || 'The requested course could not be found.'}</p>
            <button onClick={() => navigate('/ki-kurse')} className="back-btn">
              ← Back to Courses
            </button>
          </div>
        </section>
      ) : (
        <section className="course-content">
          {contentLoading || !course ? (
            <div className="course-content-loading">
              <div className="spinner"></div>
              <p>Loading course details...</p>
            </div>
          ) : (
            <div className="container">
              <div className="course-content-grid">
            
            {/* Main Content */}
            <div className="course-main">
              {/* Course Description Block */}
              {course.description && (
                <div className="course-description-block">
                  <h4 className="course-section-header">Kursbeschreibung</h4>
                  <div 
                    className="course-description-content"
                    dangerouslySetInnerHTML={{ __html: course.description }}
                  />
                </div>
              )}

              {/* Session Details (moved to top) */}
              {course.sessions && Array.isArray(course.sessions) && course.sessions.length > 0 ? (
                <div className="course-sessions">
                  <h4 className="course-section-header">{`Kursablauf mit ${course.sessions.length} Sessions`}</h4>
                  
                  {/* Detailed Session Cards */}
                  <div className="sessions-detailed">
                    <div className="sessions-list">
                      {course.sessions
                        .sort((a, b) => (a.order || 0) - (b.order || 0)) // Sort by order field
                        .map((session, index) => (
                        <div key={session.id} className="session-card">
                          <div className="session-header">
                            <div className="session-number">Session {index + 1}</div>
                            <h4>{session.title}</h4>
                            <span className={`session-type ${session.locationType.toLowerCase()}`}>
                              {session.locationType === 'ONLINE' ? '🌐 Online' : '📍 Präsenz'}
                            </span>
                          </div>
                          
                          <div className="session-details">
                            <div className="session-date">
                              <strong>Datum:</strong> {formatDate(session.date)}
                            </div>
                            <div className="session-time">
                              <strong>Zeit:</strong> {formatTime(session.startTime)} - {formatTime(session.endTime)}
                            </div>
                            <div className="session-location">
                              <strong>
                                {session.locationType === 'ONLINE' ? 'Plattform:' : 'Ort:'}
                              </strong> {session.locationType === 'ONLINE' ? 'Zoom' : session.location}
                            </div>
                            {/* Max. Teilnehmer removed per request */}
                          </div>
                          
                          {session.description && (
                            <div className="session-description">
                              <p>{session.description}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                /* Show basic course schedule if no sessions available */
                <div className="course-schedule">
                  <h4>Kursplan</h4>
                  <div className="schedule-info">
                    {course.startDate && (
                      <div className="schedule-item">
                        <strong>Startdatum:</strong> {formatDate(course.startDate)}
                      </div>
                    )}
                    {course.endDate && (
                      <div className="schedule-item">
                        <strong>Enddatum:</strong> {formatDate(course.endDate)}
                      </div>
                    )}
                    <div className="schedule-note">
                      <p>📅 Detaillierte Sitzungspläne werden bei der Anmeldung bereitgestellt.</p>
                      <p>💻 Dieser Kurs kann sowohl Online- als auch Präsenz-Sitzungen umfassen.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Course Videos (primary -> left column content) */}
              {courseVideos && courseVideos.filter(v => v.isPrimary ?? true).length > 0 && (
                <div className="course-videos-primary">
                  {courseVideos
                    .filter(v => v.isPrimary ?? true)
                    .map((v) => {
                      const embed = (() => {
                        const raw = (v.url || '').trim();
                        if (!raw) return '';
                        try {
                          const u = new URL(raw);
                          if (u.hostname.includes('youtu.be')) {
                            const id = u.pathname.replace('/', '');
                            return id ? `https://www.youtube.com/embed/${id}` : '';
                          }
                          if (u.hostname.includes('youtube.com')) {
                            const watchId = u.searchParams.get('v');
                            if (watchId) return `https://www.youtube.com/embed/${watchId}`;
                            const parts = u.pathname.split('/').filter(Boolean);
                            const idx = parts.indexOf('embed');
                            if (idx >= 0 && parts[idx + 1]) return `https://www.youtube.com/embed/${parts[idx + 1]}`;
                          }
                        } catch {}
                        return '';
                      })();
                      if (!embed) return null;
                      return (
                        <div key={v.id} className="course-video-section">
                          {v.heading ? <h4>{v.heading}</h4> : null}
                          <div className="video-container">
                            <iframe
                              src={embed}
                              title={v.heading || 'Course video'}
                              frameBorder={0}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                              className="youtube-video"
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}

              {/* Inline registration form under Ablauf */}
              {!course.isExternalRegistration && (
                <div className="course-cta-card">
                  <CourseRegistrationForm
                    preselectedCourseId={course.id}
                    preselectedCourseTitle={course.title}
                    skipSelection={true}
                  />
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="course-sidebar">
              {/* Core Information Block */}
              <div className="core-info-card">
                <h4>Kursinformationen</h4>
                <div className="core-info-grid">
                  <div className="core-info-item">
                    <svg
                      className="core-info-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path d="M16 11c1.654 0 3-1.346 3-3s-1.346-3-3-3-3 1.346-3 3 1.346 3 3 3zm-8 0c1.654 0 3-1.346 3-3S9.654 5 8 5 5 6.346 5 8s1.346 3 3 3zm0 2c-2.673 0-8 1.337-8 4v2h10v-2c0-1.242.744-2.343 1.938-3.203C10.77 12.647 8.913 13 8 13zm8 0c-.913 0-2.77-.353-3.938.797C13.256 14.657 14 15.758 14 17v2h10v-2c0-2.663-5.327-4-8-4z"/>
                    </svg>
                    <div className="core-info-content">
                      <span className="core-info-label">Zielgruppe</span>
                      <span className="core-info-value">{course.targetgroup}</span>
                    </div>
                  </div>

                  <div className="core-info-item">
                    <svg
                      className="core-info-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path d="M19 4h-1V2h-2v2H8V2H6v2H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 15H5V10h14v9Zm0-11H5V6h14v2Z"/>
                    </svg>
                    <div className="core-info-content">
                      <span className="core-info-label">Zeitraum</span>
                      <span className="core-info-value">
                        {course.startDate && course.endDate ? 
                          `${formatDate(course.startDate)} - ${formatDate(course.endDate)}` : 
                          'Daten werden geladen...'}
                      </span>
                    </div>
                  </div>

                  <div className="core-info-item">
                    <svg
                      className="core-info-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path d="M12 2c-3.314 0-6 2.686-6 6 0 5.25 6 12 6 12s6-6.75 6-12c0-3.314-2.686-6-6-6zm0 10a2 2 0 110-4 2 2 0 010 4z"/>
                    </svg>
                    <div className="core-info-content">
                      <span className="core-info-label">Ort</span>
                      <span className="core-info-value">{course.location}</span>
                    </div>
                  </div>

                  <div className="core-info-item">
                    <svg
                      className="core-info-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm1 11h-4a1 1 0 0 1 0-2h3V6a1 1 0 1 1 2 0v6a1 1 0 0 1-1 1Z"/>
                    </svg>
                    <div className="core-info-content">
                      <span className="core-info-label">Kursstunden</span>
                      <span className="core-info-value">{getTotalCourseHours(course.sessions)} Stunden</span>
                    </div>
                  </div>

                  <div className="core-info-item">
                    <svg
                      className="core-info-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path d="M4 4h16a1 1 0 0 1 1 1v3H3V5a1 1 0 0 1 1-1Zm-1 6h18v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V10Zm3 2v2h6v-2H6Zm0 4v2h10v-2H6Z"/>
                    </svg>
                    <div className="core-info-content">
                      <span className="core-info-label">Sessions</span>
                      <span className="core-info-value">{Array.isArray(course.sessions) ? course.sessions.length : 0} Sessions</span>
                    </div>
                  </div>

                  <div className="core-info-item">
                    <svg
                      className="core-info-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path d="M15 18.5c-2.51 0-4.68-1.42-5.76-3.5H15v-2H8.58c-.05-.33-.08-.66-.08-1s.03-.67.08-1H15v-2H9.24C10.32 6.92 12.5 5.5 15 5.5c1.61 0 3.09.59 4.23 1.57L21 5.3C19.41 3.87 17.3 3 15 3c-3.92 0-7.24 2.51-8.48 6H3v2h3.06c-.04.33-.06.66-.06 1 0 .34.02.67.06 1H3v2h3.52c1.24 3.49 4.56 6 8.48 6 2.31 0 4.41-.87 6-2.3l-1.78-1.77c-1.13.98-2.6 1.57-4.22 1.57z"/>
                    </svg>
                    <div className="core-info-content">
                      <span className="core-info-label">Preis</span>
                      <span className="core-info-value">{course.price} pro Person</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Videos (secondary -> right column sidebar) */}
              {courseVideos && courseVideos.filter(v => !(v.isPrimary ?? true)).length > 0 && (
                <div className="course-videos-secondary">
                  {courseVideos
                    .filter(v => !(v.isPrimary ?? true))
                    .map((v) => {
                      const embed = (() => {
                        const raw = (v.url || '').trim();
                        if (!raw) return '';
                        try {
                          const u = new URL(raw);
                          if (u.hostname.includes('youtu.be')) {
                            const id = u.pathname.replace('/', '');
                            return id ? `https://www.youtube.com/embed/${id}` : '';
                          }
                          if (u.hostname.includes('youtube.com')) {
                            const watchId = u.searchParams.get('v');
                            if (watchId) return `https://www.youtube.com/embed/${watchId}`;
                            const parts = u.pathname.split('/').filter(Boolean);
                            const idx = parts.indexOf('embed');
                            if (idx >= 0 && parts[idx + 1]) return `https://www.youtube.com/embed/${parts[idx + 1]}`;
                          }
                        } catch {}
                        return '';
                      })();
                      if (!embed) return null;
                      return (
                        <div key={v.id} className="course-video-section" style={{ marginBottom: 16 }}>
                          {v.heading ? <h4>{v.heading}</h4> : null}
                          <div className="video-container">
                            <iframe
                              src={embed}
                              title={v.heading || 'Course video'}
                              frameBorder={0}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                              className="youtube-video"
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}

              {/* Inline registration form moved to left column above */}

              {(course.partner || course.partner2) && (
                <div className="course-info-card partners-card">
                  <h4>Kooperationspartner</h4>
                  <div className="participants-grid partners-grid">
                    {course.partner?.logoPath ? (
                      <div
                        className="participant-grid-item"
                        style={{
                          background: 'transparent',
                          borderRadius: 12,
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 180,
                          cursor: course.partner.website ? 'pointer' : 'default',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        }}
                        onClick={() => course.partner && handlePartnerClick(course.partner)}
                        onMouseEnter={(e) => {
                          if (course.partner?.website) {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                        title={course.partner.website ? `Besuchen Sie ${course.partner.name}` : course.partner.name}
                      >
                        <img
                          src={partnerLogoUrl || (course.partner.logoPath.startsWith('/') || course.partner.logoPath.startsWith('http') ? course.partner.logoPath : `/assets/images/partners/${course.partner.logoPath}`)}
                          alt={`${course.partner.name} Logo`}
                          style={{ 
                            maxHeight: 170, 
                            maxWidth: '95%', 
                            width: '95%', 
                            height: '90%',
                            objectFit: 'contain',
                            filter: course.partner.website ? 'none' : 'grayscale(20%)'
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                          onDragStart={handleImageDragStart}
                          onContextMenu={handleImageContextMenu}
                          draggable={false}
                        />
                      </div>
                    ) : null}

                    {course.partner2?.logoPath ? (
                      <div
                        className="participant-grid-item"
                        style={{
                          background: 'transparent',
                          borderRadius: 12,
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 180,
                          cursor: course.partner2.website ? 'pointer' : 'default',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        }}
                        onClick={() => course.partner2 && handlePartnerClick(course.partner2)}
                        onMouseEnter={(e) => {
                          if (course.partner2?.website) {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                        title={course.partner2.website ? `Besuchen Sie ${course.partner2.name}` : course.partner2.name}
                      >
                        <img
                          src={partner2LogoUrl || (course.partner2.logoPath.startsWith('/') || course.partner2.logoPath.startsWith('http') ? course.partner2.logoPath : `/assets/images/partners/${course.partner2.logoPath}`)}
                          alt={`${course.partner2.name} Logo`}
                          style={{ 
                            maxHeight: 170, 
                            maxWidth: '95%', 
                            width: '95%', 
                            height: '90%',
                            objectFit: 'contain',
                            filter: course.partner2.website ? 'none' : 'grayscale(20%)'
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                          onDragStart={handleImageDragStart}
                          onContextMenu={handleImageContextMenu}
                          draggable={false}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              )}

              {(course.tutor || course.tutor2Id) && (
                <div className="tutors-card">
                  <h4>Dozenten</h4>
                  <div className="tutors-grid">
                    {course.tutor && (
                      <div className="tutor-grid-item"
                           style={{ cursor: 'pointer' }}
                           onClick={() => {
                             if (course.tutor?.name) {
                               const tutorSlug = course.tutor.name
                                 .toLowerCase()
                                 .replace(/[^a-z0-9äöüß]+/g, '-')
                                 .replace(/^-+|-+$/g, '');
                               navigate(`/tutors/${tutorSlug}`);
                             }
                           }}
                           title="Klicken Sie, um mehr über den Dozenten zu erfahren"
                      >
                        <div className="tutor-image-container">
                          <img
                            src={tutorPhotoCandidates[tutorPhotoIndex] || '/assets/images/tutors/default-tutor.svg'}
                            alt={`${course.tutor?.name || 'Tutor'} - Dozent`}
                            className="tutor-image"
                            onError={() => {
                              const next = tutorPhotoIndex + 1;
                              if (next < tutorPhotoCandidates.length) {
                                setTutorPhotoIndex(next);
                              }
                            }}
                            onDragStart={handleImageDragStart}
                            onContextMenu={handleImageContextMenu}
                            draggable={false}
                          />
                        </div>
                        <h4 className="tutor-name">{buildDisplayNameWithAcademicTitle(course.tutor) || 'Tutor TBD'}</h4>
                        <p className="tutor-bio">
                          {course.tutor?.role || ''}
                        </p>
                      </div>
                    )}

                    {course.tutor2Id && (
                      <div className="tutor-grid-item"
                           style={{ cursor: course.tutor2?.name ? 'pointer' : 'default' }}
                           onClick={() => {
                             if (course.tutor2?.name) {
                               const tutorSlug = course.tutor2.name
                                 .toLowerCase()
                                 .replace(/[^a-z0-9äöüß]+/g, '-')
                                 .replace(/^-+|-+$/g, '');
                               navigate(`/tutors/${tutorSlug}`);
                             }
                           }}
                           title="Klicken Sie, um mehr über den zweiten Dozenten zu erfahren"
                      >
                        <div className="tutor-image-container">
                          <img
                            src={tutor2PhotoCandidates[tutor2PhotoIndex] || '/assets/images/tutors/default-tutor.svg'}
                            alt={`${course.tutor2?.name || 'Zweiter Tutor'} - Dozent`}
                            className="tutor-image"
                            onError={() => {
                              const next = tutor2PhotoIndex + 1;
                              if (next < tutor2PhotoCandidates.length) {
                                setTutor2PhotoIndex(next);
                              }
                            }}
                            onDragStart={handleImageDragStart}
                            onContextMenu={handleImageContextMenu}
                            draggable={false}
                          />
                        </div>
                        <h4 className="tutor-name">{buildDisplayNameWithAcademicTitle(course.tutor2) || 'Zweiter Tutor'}</h4>
                        <p className="tutor-bio">
                          {course.tutor2?.role || ''}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Discount Information Block */}
              {discountInformation && (
                <div className="discount-card">
                  <h4>Rabatt-Informationen</h4>
                  <div 
                    className="discount-info-content"
                    dangerouslySetInnerHTML={{ __html: discountInformation }}
                  />
                </div>
              )}

              {/* References/Customers logos block */}
              <CourseReferencesGrid courseId={course?.id} maxReferences={24} />

              {/* Removed Kursinformationen block per request */}
            </div>
          </div>
        </div>
          )}
        </section>
      )}
    </div>
  );
};

export default CourseDetailPage;
